import { useCallback, useEffect, useRef, useState } from 'react';
import { applyVoiceEvent } from '../../lib/voice-transcript';

function atCapacity(messages) {
  return messages.length >= 118 || messages.reduce((size, item) => size + item.content.length, 0) > 42000;
}

export function useVoiceTranscript(call, mounted) {
  const [messages, setMessages] = useState([]);
  const [saveStatus, setSaveStatus] = useState('');
  const history = useRef([]), queue = useRef(Promise.resolve()), revision = useRef(0);

  const record = useCallback(event => {
    const next = applyVoiceEvent(history.current, event);
    if (next !== history.current) { history.current = next; setMessages(next); }
    return atCapacity(next);
  }, []);

  const save = useCallback(() => {
    const owner = call.current;
    const snapshot = history.current.filter(message => message.content);
    if (!owner?.ticket || !snapshot.length) return Promise.resolve(true);
    const version = ++revision.current;
    if (mounted.current) setSaveStatus('saving');
    const publish = status => {
      if (mounted.current && call.current === owner && revision.current === version) setSaveStatus(status);
    };
    const operation = queue.current.then(() => owner.request('/transcript', { messages: snapshot }));
    // Resolve failures after displaying them; a failed save must not poison later retries.
    queue.current = operation.then(
      () => { publish('saved'); return true; },
      () => { publish('error'); return false; },
    );
    return queue.current;
  }, [call, mounted]);

  const resetTranscript = useCallback(() => {
    history.current = []; revision.current += 1; queue.current = Promise.resolve();
    setMessages([]); setSaveStatus('');
  }, []);
  useEffect(() => {
    if (!messages.length) return undefined;
    const timer = setTimeout(save, 1200);
    return () => clearTimeout(timer);
  }, [messages, save]);
  return { messages, saveStatus, record, save, resetTranscript };
}